import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { VehicleDetails } from './bookingSlice'

/**
 * Cross-device booking pre-fill — the last vehicle the user picked in /book,
 * decoupled from the in-progress booking flow so it survives
 * `clearBookingState()`. Persisted client-side via redux-persist (whitelisted
 * in store.ts) and synced server-side per-user via
 * `getLastBookingVehicle` / `saveLastBookingVehicleAction`.
 *
 * Lifecycle:
 * - set on every successful vehicle pick in VehicleDetails
 * - hydrated on /book mount from User.lastBookingVehicle (server copy)
 * - cleared in useStoreUserSync on logout / user-switch (account copy untouched)
 */

export interface LastVehicleState {
  vehicleDetails: VehicleDetails | null
  selectedVehicleId: string | null
  updatedAt: string | null
}

const initialState: LastVehicleState = {
  vehicleDetails: null,
  selectedVehicleId: null,
  updatedAt: null
}

interface SetLastVehiclePayload {
  vehicleDetails: VehicleDetails
  selectedVehicleId: string | null
}

const lastVehicleSlice = createSlice({
  name: 'lastVehicle',
  initialState,
  reducers: {
    setLastVehicle: (state, action: PayloadAction<SetLastVehiclePayload>) => {
      state.vehicleDetails = action.payload.vehicleDetails
      state.selectedVehicleId = action.payload.selectedVehicleId
      state.updatedAt = new Date().toISOString()
    },
    clearLastVehicle: () => initialState
  }
})

export const { setLastVehicle, clearLastVehicle } = lastVehicleSlice.actions
export default lastVehicleSlice.reducer
